import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export interface DecisionNoteParticipant {
  id: string;
  displayName: string;
  own: boolean;
  private: boolean;
  submitted: boolean;
}

export interface DecisionNoteControls {
  instanceId: string;
  activeNoteId: string;
  participants: readonly DecisionNoteParticipant[];
  openNote: (noteId: string) => void;
}

interface DecisionNoteBridgeValue {
  controls: DecisionNoteControls | null;
  register: (controls: DecisionNoteControls) => () => void;
}

const DecisionNoteBridgeContext = createContext<DecisionNoteBridgeValue | null>(null);

/**
 * Joins the persistent huddle strip to the active Decision Maker renderer without
 * leaking callbacks or private projection state outside this PlaySurface instance.
 */
export function DecisionNoteBridgeProvider({ children }: { children: ReactNode }) {
  const [controls, setControls] = useState<DecisionNoteControls | null>(null);
  const register = useCallback((next: DecisionNoteControls) => {
    setControls(next);
    return () => {
      setControls((current) => current === next ? null : current);
    };
  }, []);
  const value = useMemo(() => ({ controls, register }), [controls, register]);
  return <DecisionNoteBridgeContext.Provider value={value}>{children}</DecisionNoteBridgeContext.Provider>;
}

export function useDecisionNoteBridge() {
  return useContext(DecisionNoteBridgeContext);
}
